import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, 
  Mail, 
  Newspaper, 
  TrendingUp,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    testimonials: 0,
    news: 0,
    contacts: 0,
    newContacts: 0
  });
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, newsRes, contactsRes] = await Promise.all([
          axios.get(`${API}/admin/testimonials`, getAuthHeaders()),
          axios.get(`${API}/admin/news`, getAuthHeaders()),
          axios.get(`${API}/admin/contacts`, getAuthHeaders())
        ]);

        const contacts = contactsRes.data;
        const newContacts = contacts.filter(c => c.status === 'new').length;

        setStats({
          testimonials: testimonialsRes.data.length,
          news: newsRes.data.length,
          contacts: contacts.length,
          newContacts
        });

        setRecentContacts(contacts.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    { label: 'Testimonials', value: stats.testimonials, icon: MessageSquare, color: 'bg-blue-500' },
    { label: 'News Items', value: stats.news, icon: Newspaper, color: 'bg-green-500' },
    { label: 'Total Enquiries', value: stats.contacts, icon: Mail, color: 'bg-purple-500' },
    { label: 'New Enquiries', value: stats.newContacts, icon: TrendingUp, color: 'bg-amber-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-serif mb-1">Welcome back!</h1>
        <p className="text-muted-foreground">Here's what's happening with your website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white p-6 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-3xl font-bold">{stat.value}</span>
              </div>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Contacts */}
      <div className="bg-white rounded-lg border border-border">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Recent Enquiries
          </h2>
        </div>
        <div className="divide-y divide-border">
          {recentContacts.length === 0 ? (
            <p className="p-4 text-muted-foreground text-center">No enquiries yet</p>
          ) : (
            recentContacts.map((contact) => (
              <div key={contact.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{contact.name}</p>
                  <p className="text-sm text-muted-foreground">{contact.email}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    contact.status === 'new' 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {contact.status}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
